import { memo, useCallback, useMemo } from 'react';

import { CustomFieldsRow } from '~/components/ShareModal/components/InfoCommentsSection/CustomFieldsRow';
import { useShowcaseModal } from '~/components/ShowcaseModal/ShowcaseModalContext';
import { type PermissionMode } from '~/components/ShowcaseModal/types';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canChangeAssetCustomFields, canSeeAssetCustomFields } from '~/utils/permissions/assetPermissions';

/** Create-mode smart leaf: custom-fields permission segmented row. Writes draft state, no per-change analytics. */
export const CreateShowcaseSettingsCustomFieldsRow = memo(() => {
  const { shortUrlProps, isSaving, updateCustomFieldPermissions } = useShowcaseModal();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const canViewCustomFields = useMemo(() => canSeeAssetCustomFields(workspacePermissions), [workspacePermissions]);
  const canEditCustomFields = useMemo(() => canChangeAssetCustomFields(workspacePermissions), [workspacePermissions]);

  const showMetadata = useMemo(
    () => shortUrlProps.presentationSettings?.hideInfoTab !== true,
    [shortUrlProps.presentationSettings?.hideInfoTab],
  );

  const handleCustomFieldsChange = useCallback(
    ({ canView, canManageApplications }: { canView: boolean; canManageApplications: boolean }) => {
      let mode: PermissionMode;

      if (canView && canManageApplications) {
        mode = 'allow';
      } else if (canView) {
        mode = 'read-only';
      } else {
        mode = 'hide';
      }

      updateCustomFieldPermissions(mode);
    },
    [updateCustomFieldPermissions],
  );

  return (
    <CustomFieldsRow
      canEditCustomFields={canEditCustomFields}
      canManageApplications={shortUrlProps.permissions.customField.canManageApplications}
      canView={shortUrlProps.permissions.customField.canView}
      canViewCustomFields={canViewCustomFields}
      data-testid="CUSTOM_FIELDS_SEGMENTED_CONTROL"
      isLoading={isSaving}
      onChange={handleCustomFieldsChange}
      showMetadata={showMetadata}
    />
  );
});

CreateShowcaseSettingsCustomFieldsRow.displayName = 'CreateShowcaseSettingsCustomFieldsRow';
